import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ConfirmDraftActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  variant?: 'default' | 'destructive';
  onConfirm: () => void | Promise<void>;
  /** When true, the confirm button shows a spinner and both buttons are disabled.
   *  Caller manages this around the awaited action; the dialog does not auto-close
   *  on confirm, so the caller is also responsible for dismissing via onOpenChange. */
  isLoading?: boolean;
}

// Generic confirm dialog for archive / restore / delete actions in the quotation
// flow. Mirrors ConfirmDeleteDraftDialog's shape but adds variant + isLoading and
// drops the auto-close-on-confirm so callers can keep the dialog visible while
// the async action runs.
export function ConfirmDraftActionDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  variant = 'default',
  onConfirm,
  isLoading = false,
}: ConfirmDraftActionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !isLoading && onOpenChange(next)}>
      <DialogContent hideCloseButton>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            disabled={isLoading}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            disabled={isLoading}
            className={cn(
              variant === 'destructive' && 'text-destructive hover:bg-destructive/10',
            )}
            onClick={() => {
              void onConfirm();
            }}
          >
            {isLoading && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
